<template>
  <div>
    <mainHeader></mainHeader>
    <testPortalBanner v-if="isTestPortal"></testPortalBanner>
    <sideMenu :studyData="studyData" :isTestPortal="isTestPortal"></sideMenu>
    <div class="has-sidemenu has-header" :class="{ 'test-portal-padding-reset': isTestPortal }">
      <navBar v-if="isDetailPage" :routes="navBarRoutes" />
      <router-view :studyData="studyData" :studyId="studyId"></router-view>
    </div>
  </div>
</template>

<script>
import mainHeader from "@/components/common/main-header";
import sideMenu from "@/components/common/side-menu";
import navBar from "@/components/common/nav-bar";
import testPortalBanner from "@/components/common/test-portal-banner";
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import { RATER, SYSTEM_ADMIN, STUDY_ADMIN, PI, SUB_PI, STUDY_COORDINATOR, HOME_HEALTH, QUALITY, SITEDATALOCK, STUDY_MANAGER, INTERVIEWER, CC, SUPPORT } from "@/constants/authRoles";
import { TEST_PORTAL_SUFFIX } from "@/constants/config";
import { AclRule } from "vue-acl";
import localStorageService from "@/services/localStorage";
import { canStudyTeamOrAdminSiteAccessSiteDocuments } from "@/services/rolesService";

export default {
  name: "study-participants-page-wrapper",
  components: {
    mainHeader,
    sideMenu,
    navBar,
    testPortalBanner
  },
  data() {
    return {
      studyData: {},
      toggleFeatures: [],
      teleHealthConfigured: false,
      edcEnabled: false,
      telehealthEnabled: false,
      isTelehealthV2Enabled: false,
      sites: []
    };
  },
  computed: {
    ...mapGetters(["user"]),
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canView() {
      return this.studyData.truClinicIntegration;
    },
    canTelehealthView() {
      return this.studyData.telehealthIntegration;
    },
    canCallCenterAccess() {
      this.$acl.change(`${CC}`);
      return this.$acl.check("userAccess");
    },
    canSysAdminAccess() {
      this.$acl.change(`${SYSTEM_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canStudyAdminAccess() {
      this.$acl.change(`${STUDY_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canStudyManagerAccess() {
      this.$acl.change(`${STUDY_MANAGER}`);
      return this.$acl.check("userAccess");
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canSubPiAccess() {
      this.$acl.change(`${SUB_PI}`);
      return this.$acl.check("userAccess");
    },
    canStudyCoordinatorAccess() {
      this.$acl.change(`${STUDY_COORDINATOR}`);
      return this.$acl.check("userAccess");
    },
    canQualityAccess() {
      this.$acl.change(`${QUALITY}`);
      return this.$acl.check("userAccess");
    },
    canHomeHealthAccess() {
      this.$acl.change(`${HOME_HEALTH}`);
      return this.$acl.check("userAccess");
    },
    canInterviewerAccess() {
      this.$acl.change(`${INTERVIEWER}`);
      return this.$acl.check("userAccess");
    },
    canRaterAccess() {
      this.$acl.change(`${RATER}`);
      return this.$acl.check("userAccess");
    },
    canSupportAccess() {
      this.$acl.change(`${SUPPORT}`);
      return this.$acl.check("userAccess");
    },
    canSiteTeamSiteDocumentAccess() {
      return this.canSiteTeamMemberAccess && !this.canStudyAdminAccess && !this.canRaterAccess && !this.canCallCenterAccess && !this.canSiteDataLockAccess && !this.canSupportAccess;
    },
    canStudyTeamOrAdminSiteDocumentAccess() {
      return canStudyTeamOrAdminSiteAccessSiteDocuments();
    },
    isTelehealthEnabledForCountry() {
      if (this.user.userRole === "Interviewer") {
        return true;
      } else {
        return (
          this.user.countries &&
          _.chain(this.user.countries)
            .filter({ isTelehealthEnabled: true })
            .size()
            .value() > 0
        );
      }
    },
    canSiteTeamMemberAccess() {
      return this.canPiAccess || this.canSubPiAccess || this.canHomeHealthAccess || this.canStudyCoordinatorAccess || this.canSiteDataLockAccess;
    },
    studyId() {
      return this.$route.params.studyId;
    },
    isDetailPage() {
      let noTabPages = ["study-participants-list"];
      if (this.edcEnabled) {
        noTabPages.push("study-participants-edc-data-dashboard");
      }
      if (this.telehealthEnabled && this.teleHealthConfigured) {
        noTabPages.push("study-participants-data-dashboard");
      }
      noTabPages.push("study-participant-documents");
      noTabPages.push("study-participant-messages");
      noTabPages.push("study-participant-compose-message");
      noTabPages.push("study-participant-appointments");
      noTabPages.push("study-participant-availability");

      if (this.isTestPortal) {
        noTabPages = noTabPages.map(page => `${TEST_PORTAL_SUFFIX}${page}`);
      }

      return noTabPages.includes(this.$route.name);
    },
    navBarRoutes() {
      let routes = [
        {
          label: "Participant List",
          to: {
            name: "study-participants-list",
            params: { studyId: this.studyId }
          }
        }
      ];
      if (this.edcEnabled && !this.canRaterAccess && !this.canSupportAccess) {
        routes.push({
          label: "Data Dashboard",
          to: {
            name: "study-participants-edc-data-dashboard",
            params: { studyId: this.studyId }
          }
        });
      }
      if (this.telehealthEnabled && this.teleHealthConfigured && !this.canRaterAccess && !this.canSupportAccess) {
        routes.push({
          label: "Data Dashboard",
          to: {
            name: "study-participants-data-dashboard",
            params: { studyId: this.studyId }
          }
        });
      }
      if (this.canStudyTeamOrAdminSiteDocumentAccess || this.canSiteTeamSiteDocumentAccess) {
        routes.push({
          label: "Site Documents",
          to: {
            name: "study-participant-documents",
            params: { studyId: this.studyId }
          }
        });
      }
      if ((this.canSiteTeamMemberAccess || this.canCallCenterAccess) && !this.canSiteDataLockAccess && !this.canRaterAccess && !this.canSupportAccess) {
        routes.push({
          label: "Messages",
          to: {
            name: "study-participant-messages",
            params: { studyId: this.studyId }
          }
        });
      }
      /* New visits component */
      if (this.canTelehealthView && this.isTelehealthV2Enabled && (this.canPiAccess || this.canSubPiAccess || this.canStudyCoordinatorAccess || this.canHomeHealthAccess || this.canSiteDataLockAccess || this.canInterviewerAccess || this.user.readOnly || this.canCallCenterAccess) && this.isTelehealthEnabledForCountry) {
        routes.push({
          label: "Visits",
          to: {
            name: "study-participant-appointments",
            params: { studyId: this.studyId }
          }
        });
      }

      if (this.isTestPortal) {
        routes = routes.map(route => ({
          ...route,
          to: {
            ...route.to,
            name: `${TEST_PORTAL_SUFFIX}${route.to.name}`
          }
        }));
      }

      return routes;
    },
    canSiteDataLockAccess() {
      this.$acl.change(`${SITEDATALOCK}`);
      return this.$acl.check("userAccess");
    }
  },
  created() {
    this.getToggledFeatures();
  },
  mounted() {
    if (this.user.userRole === "Interviewer") {
      this.getDefaultSite();
    }

    try {
      this.studyData = localStorageService.getItem("studyDetailsData")[this.studyId] || {};
    } catch (e) {
      console.error(e);
      this.studyData = {};
    }

    this.teleHealthConfigured = this.studyData && this.studyData.truClinicIntegration;
  },
  methods: {
    ...mapActions(["setStudyToggledFeaturesData"]),
    getToggledFeatures() {
      this.toggleFeatures = this.$store.getters.getStudyToggledFeatures ? this.$store.getters.getStudyToggledFeatures : JSON.parse(window.localStorage.getItem("toggledFeatures"));
      this.toggleFeatures.forEach(feature => {
        if (feature.featureKey === "EDC") {
          this.edcEnabled = feature.enabled;
        }
        if (feature.featureKey === "TruClinic") {
          this.telehealthEnabled = feature.enabled;
        }
        if (feature.featureKey === "TELEHEALTH") {
          this.isTelehealthV2Enabled = feature.enabled;
        }
      });
    },
    async getDefaultSite() {
      this.sites = [];
      await this.$api.getSitesData(this.studyId).then(response => {
        if (response) {
          const sitesdata = response.body;
          sitesdata.map(el => {
            this.sites.push(el);
          });
        }
      });
      this.sites.filter(el => {
        if (el.isDefault === true) {
          localStorage.setItem("selectedSite", el.id);
        }
      });
    }
  },
  props: {
    isTestPortal: {
      type: Boolean,
      default: false
    }
  }
};
</script>
<style scoped>
@media (min-width: 768px) and (max-width: 991px) {
  .nav-margin-md {
    margin: 0 20px !important;
  }
}
</style>
